/**
 * MANAGEMENT (15) — analyzer-fleet data layer. The two CROSS-LAB, READ-ONLY monitoring reads behind the
 * platform Analyzer-Fleet console. Code-verified against server/src/modules/management/analyzers.routes.js:
 *   GET /management/analyzers                     management.analyzer.manage  → AnalyzerFleetPage
 *   GET /management/analyzers/:automateId/health   management.analyzer.manage  → AnalyzerHealth
 *
 * CROSS-LAB, NOT lab-scoped — the keys live under the shared `["platform","management","analyzers",…]`
 * namespace (a lab switch never invalidates them; one operator session monitors the whole estate), mirroring
 * the sibling `metricsKeys` / `managementKeys` factories. Reads follow the established convention: gate first
 * (`enabled = useIsPlatformAdmin() && online`), then branch on data. A cloud-only route 404/501 on a local
 * box is surfaced via the shared `isManagementAbsent` (re-exported from ../api). The fleet plane is
 * monitoring-only — there are NO mutations here (no create/update/delete verb; the per-lab `lis` module owns
 * every write to a lab's instruments).
 */
import { useApiQuery } from "@/api";
import type { AnalyzerFleetFilters, AnalyzerFleetPage, AnalyzerHealth } from "./analyzers.types";

/** Strip undefined/null/empty so the cache key is stable and the server gets a clean query. */
function cleanQuery(filters: object): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") out[k] = v as string | number | boolean;
  }
  return out;
}

/**
 * Map the camelCase filter object → the snake_case query string the validator expects
 * (`laboratoire_id` / `discipline_code` / `page_size`). Only present keys are emitted.
 */
function toQuery(filters: AnalyzerFleetFilters): Record<string, string | number | boolean> {
  return cleanQuery({
    q: filters.q,
    laboratoire_id: filters.laboratoireId,
    discipline_code: filters.disciplineCode,
    statut: filters.statut,
    // The server accepts the boolean-ish "true"/"false" string; only send when the operator picked a side.
    online: filters.online === undefined ? undefined : filters.online ? "true" : "false",
    page: filters.page,
    page_size: filters.pageSize,
  });
}

/** The analyzer-fleet query keys — rooted under the platform/management namespace, NOT lab-scoped. */
export const analyzerKeys = {
  root: ["platform", "management", "analyzers"] as const,
  fleet: (filters: AnalyzerFleetFilters = {}) =>
    ["platform", "management", "analyzers", "fleet", filters] as const,
  health: (automateId: string, days: number) =>
    ["platform", "management", "analyzers", "health", automateId, days] as const,
};

/**
 * GET /management/analyzers — the cross-lab fleet list (lab + discipline + protocol/transport + statut + last
 * heartbeat + online flag + fiabilite), filtered + server-paginated. A 30s staleTime keeps the fleet warm
 * without hammering the estate; the heartbeat freshness the server derives is the live-ish signal.
 */
export function useAnalyzerFleet(filters: AnalyzerFleetFilters, enabled: boolean) {
  return useApiQuery<AnalyzerFleetPage>(analyzerKeys.fleet(filters), "/management/analyzers", {
    query: toQuery(filters),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * GET /management/analyzers/:automateId/health — one analyzer's health/usage drill-down over a trailing day
 * window. `enabled` folds in the platform gate AND the "a drawer is open on a real id" condition, so no fetch
 * fires while the drawer is closed. A 30s staleTime; the daily snapshots are upsert-per-day so they move
 * slowly relative to the KPI counts.
 */
export function useAnalyzerHealth(automateId: string | null, days: number, enabled: boolean) {
  const id = automateId ?? "0";
  return useApiQuery<AnalyzerHealth>(
    analyzerKeys.health(id, days),
    `/management/analyzers/${id}/health`,
    { query: { days }, enabled: enabled && automateId != null, staleTime: 30_000 },
  );
}
